import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './Start.css';

function SurveyForm() {
  const navigate = useNavigate();
  const TOTAL_QUESTIONS = 20;
  const [answers, setAnswers] = useState({});
  const [optionWeights, setOptionWeights] = useState({});
  const [groupWeights, setGroupWeights] = useState({});
  const [questions, setQuestions] = useState([]);
  const [firstGroupCount, setFirstGroupCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return; 
    if (questions.length === 0) {
      setLoading(true);
      fetch("https://api.nagae.biz/api/survey", {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
        .then(response => {
          if (!response.ok) throw new Error("Failed to fetch survey data");
          return response.json();
        })
        .then(data => {
          setOptionWeights(data.option_weights);
          setGroupWeights(data.group_weights1);
          setQuestions(data.questions1);
          setFirstGroupCount(data.questions1.length);
          setLoading(false);
        })
        .catch(error => {
          console.error("Error fetching survey data:", error);
          setErrors({ fetch: "アンケートの読み込みに失敗しました" });
          setLoading(false);
        });
    }
  }, [questions, token]);

  const handleAnswer = (optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: optionIndex,
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      if (questions.length === firstGroupCount) {
        setLoading(true);
        fetch("https://api.nagae.biz/api/survey2", {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        })
          .then(response => {
            if (!response.ok) throw new Error("Failed to fetch survey2 data");
            return response.json();
          })
          .then(data => {
            if (data && data.questions2) {
              setQuestions(prev => [...prev, ...data.questions2]);
              if (data.option_weights2) setOptionWeights(data.option_weights2);
              if (data.group_weights2) setGroupWeights(data.group_weights2);
              setCurrentQuestionIndex(currentQuestionIndex + 1);
              setLoading(false);
            }
          })
          .catch(error => {
            console.error("Error fetching survey2 data:", error);
            setErrors({ fetch: "次のアンケートの読み込みに失敗しました" });
            setLoading(false);
          });
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = () => {
    const combinedAnswers = questions.map((q, i) => ({
      questionNumber: i + 1,
      selectedOption: Number(answers[i])
    }));

    const payload = { answers: combinedAnswers };

    setLoading(true);
    fetch("https://api.nagae.biz/api/calculate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) throw new Error("Failed to submit survey answers");
        return response.json();
      })
      .then(data => {
        sessionStorage.setItem('Score1', data.score1);
        sessionStorage.setItem('Score2', data.score2);
        sessionStorage.setItem('Score2_display', data.score2_display);
        sessionStorage.setItem('TotalScore', data.totalScore);
        sessionStorage.setItem('tags_stage1', JSON.stringify(data.tags_stage1));
        sessionStorage.setItem('tags_stage2', JSON.stringify(data.tags_stage2));
        sessionStorage.setItem('answeredQuestions', JSON.stringify(combinedAnswers));
        sessionStorage.setItem('tags_result', JSON.stringify(data.tags_result));
        navigate('/ROI');
        setLoading(false);
      })
      .catch(error => {
        console.error("Error submitting survey answers:", error);
        setErrors({ submit: "アンケートの送信に失敗しました" });
        setLoading(false);
      });
  };

  const currentQuestionNumber = currentQuestionIndex + 1;
  const progressPercentage = Math.round((currentQuestionNumber / TOTAL_QUESTIONS) * 100);
  const currentQuestion = questions[currentQuestionIndex] || {};
  const sectionTitle = currentQuestionIndex < firstGroupCount ? "業務の構造化レベル診断" : "人間の介入度診断";

  if (loading) {
    return (
      <div className="loading-indicator">
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <header className="header">
        <div className="logo">
          <h1></h1>
          <p className="subtitle" style={{ marginBottom: '10px' }}></p>
        </div>
      </header>
      <main className="main-content">
        <div className="card" style={{ marginTop: '-50px' }}>
          <div className="progress-container">
            <div className="progress-info">
              <span id="question-number">質問 {currentQuestionNumber} / {TOTAL_QUESTIONS}</span>
              <span id="progress-percentage">{progressPercentage}%</span>
            </div>
            <div className="progress-bar">
              <div
                id="progress-bar"
                className="progress-bar-inner"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>

          <h2 className="section-title">{sectionTitle}</h2>
          <div id="question-container" className="question-container">
            <h3 className="question-text">
              {currentQuestion.question || "質問がロードされています..."}
            </h3>
            <div id="options" className="options">
              {currentQuestion.options &&
                Object.entries(currentQuestion.options).map(([key, value], i) => (
                  <label
                    key={i}
                    className={`option ${answers[currentQuestionIndex] === i ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`q${currentQuestionNumber}`}
                      className="option-radio"
                      value={i}
                      checked={answers[currentQuestionIndex] === i}
                      onChange={() => handleAnswer(i)}
                    />
                    <span>{String.fromCharCode(65 + i)}.</span>{" "}
                    {(typeof value === 'object' && value !== null ? value.text : value) || "選択肢を入力してください"}
                  </label>
                ))}
            </div>
          </div>
          <div className="button-container">
            <button
              id="prev-button"
              className="btn btn-secondary"
              disabled={currentQuestionIndex === 0}
              onClick={handlePrev}
            >
              前へ
            </button>
            <button
              id="next-button"
              className="btn btn-primary"
              disabled={answers[currentQuestionIndex] === undefined}
              onClick={handleNext}
            >
              次へ
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SurveyForm;
